import React, { FunctionComponent, useCallback, useMemo } from 'react';

import PSHeader from '@ps-int/ps-header/src';
import type { CorpService as PSCorpService, Service as PSService } from '@ps-int/ps-header/src/header';
import yaTeamDomains from '@ps-int/yandex-services/dist/domains/yandex-team.json';
import {
    AnyService,
    GlobalBarControl,
    GlobalBar as GlobalBarLocal,
    MonetizationItem as MonetizationItemLocal,
} from '@yandex-360/global-bar';
import { Settings as SettingsIcon } from '@yandex-360/iconostasis/dist/icons/Settings';
import { ScopedTheme } from '@yandex-360/orb-components/dist/components/Theme/ScopedTheme';
import { PressEvent } from '@yandex-360/orb-components/dist/types';
import cn from 'classnames';
import { connect, ConnectedProps, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { RootState } from '../../../../init-store';
import { YaServicesConfig } from '../../features/config';
import {
    getExperimentsFlags,
    isLightTheme,
    selectGlobalBarInitData,
    selectIsGlobalBarEnabled,
    selectSelectedLogin,
    selectSelectedUid,
} from '../../features/config/selectors';
import {
    selectIsCorpHeaderEnabled,
    selectIsSmartGlobalBarEnabled,
    selectIsTestplane,
} from '../../features/flags/selectors';
import { ModuleFederationComponents } from '../../features/module-federation/constants';
import { moduleFederation } from '../../features/module-federation/module-federation';
import { openSettingsModalAction } from '../../features/settings-modal/slice';
import { toPassport } from '../../features/user';
import { getLogoOrganization, isYandexSsoUserSelector } from '../../features/user/selectors';
import { withErrorBoundary } from '../../helpers/hocs/with-error-boundary';
import { i18n } from '../../helpers/i18n';
import { createLogger } from '../../helpers/loggerFacade';
import { countMetrika, countMetrikaGlobalBar } from '../../helpers/metrika';
import { useActions } from '../../hooks';
import Button from '../button';
import { Popover } from '../popover';
import css from './index.css';

const logger = createLogger('header');

const mapState = (state: RootState) => {
    const {
        user,
        ua: { isMobile, BrowserName },
        config: {
            errorPageType,
            appLang,
            yandexuid,
            avatarsOrigin,
            tld,
            yaServices,
            currentHost,
            geoTimezone,
            environment,
        },
    } = state;
    return {
        user,
        isMobile,
        yandexuid,
        avatarsOrigin,
        tld,
        yaServices,
        lang: appLang,
        currentHost,
        geoTimezone,
        BrowserName,
        showFan: Boolean(user?.isB2b),
        isLightTheme: isLightTheme(state),
        errorPageType,
        environment,
        isTestplane: selectIsTestplane(state),
        expFlags: getExperimentsFlags(state),
    };
};

const mapDispatch = { toPassport };

const connector = connect(mapState, mapDispatch);

type HeaderConnectedProps = ConnectedProps<typeof connector> & { hidden?: boolean };

const getLinks = (yaServices: Record<keyof YaServicesConfig, string>, isCorp: boolean): Record<PSService, string> => ({
    Admin: yaServices.admin,
    Calendar: isCorp ? yaTeamDomains.calendar.url : yaServices.calendar,
    Contact: isCorp ? yaTeamDomains.mail.url + '/#contacts' : yaServices.mail + '/#contacts',
    Concept: yaServices.concept,
    Disk: yaServices.disk,
    Fan: yaServices.fan,
    Mail: isCorp ? yaTeamDomains.mail.url : yaServices.mail,
    Messenger: isCorp ? yaTeamDomains.q.url : yaServices.q,
    Notes: yaServices.notes,
    Premium: `${yaServices.yandex360}/business/telemost`,
    Telemost: yaServices.telemost,
    Documents: yaServices.documents,
    Tracker: yaServices.tracker,
    Wiki: yaServices.wiki,
    Forms: isCorp ? yaTeamDomains.forms.url : yaServices.forms,
    NeuroExpert: yaServices.neuroExpert,
});

const getLinksOverride = (
    yaServices: Record<keyof YaServicesConfig, string>,
    isCorp: boolean,
    selectedUid?: string,
    selectedLogin?: string
): Maybe<Record<Extract<AnyService, 'Messenger'>, string>> => {
    try {
        const links = getLinks(yaServices, isCorp);
        const messengerUrl = new URL(links.Messenger);
        if (selectedUid) {
            messengerUrl.searchParams.set('selectedUid', selectedUid);
        }
        if (selectedLogin) {
            messengerUrl.searchParams.set('selectedLogin', selectedLogin);
        }
        return {
            Messenger: messengerUrl.toString(),
        };
    } catch (error) {
        logger.warn(error, { method: 'getLinksOverride' });
        return;
    }
};

const CORP_LINKS: Record<PSCorpService, string> = {
    At: yaTeamDomains.diary.url,
    Maillists: yaTeamDomains.maillist.url,
    MeetingRooms: `${yaTeamDomains.calendar.url}/invite`,
    Staff: yaTeamDomains.staff.url,
    Startrek: yaTeamDomains.startrek.url,
    Wiki: yaTeamDomains.wiki.url,
    YTeam: yaTeamDomains.yteam.url,
};

const HeaderComponent: FunctionComponent<HeaderConnectedProps> = ({
    lang,
    tld,
    yaServices,
    user,
    isMobile,
    yandexuid,
    avatarsOrigin,
    currentHost,
    geoTimezone,
    toPassport,
    BrowserName,
    hidden,
    showFan,
    isLightTheme,
    errorPageType,
    environment,
    isTestplane,
    expFlags,
}) => {
    const handleLoginClick = useCallback(() => toPassport(), [toPassport]);

    const actions = useActions({ openSettingsModalAction });
    const onClickSettings = (event: PressEvent) => {
        actions.openSettingsModalAction({
            isHiddenSectionVisible: event.altKey,
        });
    };

    const loginButton = useMemo(
        () => (
            <Button testId="login-button" view="neutral" size="md" onClick={handleLoginClick}>
                {i18n('common', 'login')}
            </Button>
        ),
        [handleLoginClick]
    );

    const globalBarInitData = useSelector(selectGlobalBarInitData);
    const isSmartGlobalBarEnabled = useSelector(selectIsSmartGlobalBarEnabled);
    const isGlobalBarEnabled = useSelector(selectIsGlobalBarEnabled);
    const isYandexSso = useSelector(isYandexSsoUserSelector);
    const isCorpHeaderEnabled = useSelector(selectIsCorpHeaderEnabled);
    const shouldUseCorpBar = Boolean(isYandexSso || (isCorpHeaderEnabled && user?.isYandexTeamUser));

    const { pathname, search } = useLocation();
    const enabledNoB2cTariffPromo = user?.enabledNoB2cTariffPromo;

    const logoOrganization = useSelector(getLogoOrganization);
    const selectedUid = useSelector(selectSelectedUid);
    const selectedLogin = useSelector(selectSelectedLogin);

    const isLivePage = pathname.startsWith('/live/');

    const forceDarkTheme = isLivePage && !errorPageType;
    const theme = isLightTheme && !forceDarkTheme ? 'light' : 'dark';

    const retpath = `https://${currentHost}${pathname}${search}`;

    if (isGlobalBarEnabled) {
        const globalBarModule = moduleFederation?.getComponent<ModuleFederationComponents.GlobalBar>(
            ModuleFederationComponents.GlobalBar
        );
        const GlobalBar = globalBarModule?.GlobalBar ?? GlobalBarLocal;
        const MonetizationItem = globalBarModule?.MonetizationItem ?? MonetizationItemLocal;

        return (
            <ScopedTheme theme={theme}>
                <GlobalBar
                    initData={globalBarInitData}
                    is669Sid={user?.isYandexTeamUser}
                    isSsoYandexTeam={user?.isYandexSsoUser}
                    preferCorp={isCorpHeaderEnabled}
                    currentServiceLink="/"
                    isB2bAdmin={Boolean(user?.isConnectAdmin)}
                    isB2bUser={Boolean(user?.isB2b)}
                    currentService="Telemost"
                    tld={tld}
                    lang={lang}
                    timeZone={user?.timezone || geoTimezone}
                    uid={user?.uid}
                    userProps={
                        user
                            ? {
                                  colorScheme: theme,
                                  userData: {
                                      avatarId: user.avatarId,
                                      plus: user.hasPlus,
                                  },
                                  retpath,
                                  env: ['testing', 'development'].includes(environment) ? 'test' : 'prod',
                                  queryParams: {
                                      origin: 'b2b360',
                                  },
                                  // Почта не умеет yandex-team SSO - отключим счётчик непрочитанных
                                  fetchMail: !isYandexSso,
                                  platform: isMobile ? 'touch' : 'desktop',
                              }
                            : undefined
                    }
                    className={cn(css.globalBar, { [css.hidden]: hidden })}
                    countMetrikaFn={countMetrikaGlobalBar}
                    hideTuning={enabledNoB2cTariffPromo}
                    {...(!isSmartGlobalBarEnabled && {
                        showFan,
                        showBoards: !isYandexSso,
                        logoProps: {
                            href:
                                enabledNoB2cTariffPromo || user?.isB2b || user?.hasProSubscription
                                    ? '/'
                                    : yaServices.yandex360,
                            imageUrl: logoOrganization,
                        },
                    })}
                    bottomSlot2={
                        enabledNoB2cTariffPromo || shouldUseCorpBar ? null : (
                            <MonetizationItem autoplay={!user?.hasProSubscription && !isTestplane} />
                        )
                    }
                    bottomSlot1={
                        isLivePage ? null : (
                            <GlobalBarControl
                                testId="settings-button"
                                tooltipContent={i18n('settings', 'title')}
                                onPress={onClickSettings}
                            >
                                <SettingsIcon variant="solid" size="md" />
                            </GlobalBarControl>
                        )
                    }
                    onLoginClick={handleLoginClick}
                    features={expFlags}
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore сломана типизация в шапке, просит всю мапу сервисов, вместо переопределяемых
                    linksOverride={getLinksOverride(yaServices, shouldUseCorpBar, selectedUid, selectedLogin)}
                />
            </ScopedTheme>
        );
    }

    const MFHeader =
        moduleFederation?.getComponent<ModuleFederationComponents.PSHeader>(ModuleFederationComponents.PSHeader)
            ?.default ?? PSHeader;

    return (
        <ScopedTheme theme={theme}>
            <MFHeader
                links={getLinks(yaServices, shouldUseCorpBar)}
                corpLinks={CORP_LINKS}
                yandex360Link={
                    enabledNoB2cTariffPromo || user?.isB2b || user?.hasProSubscription ? '/' : yaServices.yandex360
                }
                allServicesLink={yaServices.all}
                isB2bAdmin={Boolean(user?.isConnectAdmin)}
                isB2bUser={Boolean(user?.isB2b)}
                isPDD={Boolean(user?.hosted)}
                currentService="Telemost"
                tld={tld}
                customLogoUrl={logoOrganization}
                lang={lang}
                timeZone={user?.timezone || geoTimezone}
                userProps={
                    user
                        ? {
                              uid: user.uid,
                              name: user.name,
                              avatarId: user.avatarId,
                              yu: yandexuid,
                              avatarHost: avatarsOrigin,
                              retpath,
                              yaplusAvailable: !enabledNoB2cTariffPromo,
                              hasPlus: user.hasPlus,
                              env: ['testing', 'development'].includes(environment) ? 'test' : 'prod',
                              queryParams: {
                                  origin: 'b2b360',
                              },
                              // Почта не умеет yandex-team SSO - отключим счётчик непрочитанных
                              noCounter: isYandexSso,
                              platform: isMobile ? 'touch' : 'desktop',
                          }
                        : undefined
                }
                noUserName
                LegoPopup={Popover}
                LegoButton={Button}
                rightContent={!user && loginButton}
                theme={isLightTheme && !forceDarkTheme ? 'light' : 'dark'}
                className={cn({ [css.PSHeader_hidden]: hidden }, { [css.mobilePsHeader]: isMobile })}
                countMetrikaFn={countMetrika}
                // в IE не работает интерактивность, так как под него не траспайлим бандл
                hideMore={BrowserName === 'MSIE'}
                showDocuments
                tuning={enabledNoB2cTariffPromo ? 'none' : 'on-top'}
                showFan={showFan}
                showBoards={!isYandexSso}
                useLegoV3
                useUserID
                isOrbTheme
                isNewIcons
            />
        </ScopedTheme>
    );
};

export const Header = withErrorBoundary(connector(HeaderComponent));
